/**
 * gpu_dispose — free GPU resources AFTER the frame that last used them.
 *
 * THE WARP-TEARDOWN LAW (live-traced on real GPUs, 2026-08-12): disposing a
 * geometry / material / texture while a command buffer that references it is
 * still in flight raises "Buffer used in submit while destroyed" on the WebGPU
 * renderer and fails the async pipeline creations riding the same submit
 * (swiftshader never shows it). SpaceModule solved it with a per-module
 * `disposeLater` + a 350 ms flush; this is the shared form for every module
 * that tears scenery down mid-play (living-events stages, battle bodies,
 * combat VFX). Call `disposeLater(x)` instead of `x.dispose()` — the queue
 * flushes itself a few frames later.
 */
import { nextFrame } from './frame_clock.js';

export interface Disposable { dispose(): void }

/**
 * Run `fire` only after BOTH `frames` rendered frames (rAF) AND `minMs` of
 * wall-clock have passed. WHY frames: during a world swap frames run MULTI-
 * SECOND — a 350 ms wall-clock timer fired while the swap frame's command
 * buffer was still in flight ("Buffer used in submit while destroyed" ×500,
 * descent field report 2026-08-23). Counting rendered frames guarantees the
 * submits that referenced the resources have completed. A hidden tab (no
 * rAF — but no submits either) falls back to the timer path; a 20 s
 * backstop stops anything leaking if rAF stalls.
 */
export function afterSubmitWindow(fire: () => void, frames = 6, minMs = 800): void {
  let done = false;
  const go = () => { if (!done) { done = true; fire(); } };
  let n = 0;
  const t0 = performance.now();
  const tick = () => {
    if (done) return;
    if (++n >= frames) {
      const left = minMs - (performance.now() - t0);
      if (left > 0) window.setTimeout(go, left); else go();
      return;
    }
    nextFrame(tick);
  };
  // XR P0.5 (2026-09-28): the frames are counted on whichever loop renders — the window's rAF is silent for a whole
  // immersive session, so in VR not one queued resource was ever freed (lib/frame_clock.ts).
  nextFrame(tick);
  // Hidden tab: no rAF — but no submits either, so the timer path is safe.
  // Re-arms so a tab hidden AFTER the queue still frees its batch.
  const hiddenCheck = () => {
    if (done) return;
    if (document.hidden) { go(); return; }
    window.setTimeout(hiddenCheck, 2000);
  };
  window.setTimeout(hiddenCheck, Math.max(2000, minMs));
  // NO unconditional wall-clock backstop (field flood, battle-end
  // 2026-08-23): during a 20+ s main-thread stall the old 20 s timer
  // disposed batches whose rendered-frame window had NEVER elapsed —
  // inside the stalled frame's own in-flight submit. A visible tab always
  // renders eventually; the frame chain is the only correct owner.
}

const DISPOSE_MIN_MS = 800; // compile-storm margin (deferred submits land late under pipeline pressure)
let open: Disposable[] | null = null;

/**
 * Queue a GPU resource for disposal after the in-flight frames complete.
 * Null-safe. Each batch SEALS on the next rendered frame: with one rolling
 * batch, an item pushed just before the window fired got ~zero frames of
 * protection (the post-eject cascade's second mechanism, 2026-08-23) —
 * sealing per frame gives every item its full 4-frame window.
 */
export function disposeLater(d: Disposable | null | undefined): void {
  if (!d) return;
  if ((globalThis as { __noFree?: boolean }).__noFree) return; // DIAG: leak-everything differential (hyperwarp-flood bisection)
  if (!open) {
    const batch: Disposable[] = [];
    open = batch;
    nextFrame(() => { if (open === batch) open = null; });
    afterSubmitWindow(() => {
      if (open === batch) open = null; // hidden tab: rAF never sealed it
      for (const x of batch) { try { x.dispose(); } catch { /* already freed */ } }
    }, 6, DISPOSE_MIN_MS);
  }
  open.push(d);
}

/**
 * The SECTOR's window (SpaceModule.flushDisposeSoon's own 12 frames + 3 s: a system swap's compile storm submits
 * command buffers frames after they were encoded) for a lib that frees at sector teardown — its objects leave the scene
 * NOW, their GPU handles after the window (bug scan 2026-10-08: the nebulae, the lens, the cloud maps and a disc's
 * table were freed in the tick the root left). Null-safe; never throws.
 */
export function disposeAfterSwap(...ds: Array<Disposable | null | undefined>): void {
  if ((globalThis as { __noFree?: boolean }).__noFree) return; // DIAG (see disposeLater)
  afterSubmitWindow(() => { for (const d of ds) { try { d?.dispose(); } catch { /* already freed */ } } }, 12, 3000);
}

/** Queued-but-not-yet-freed count for the CURRENT open batch (probe surface). */
function pendingOpenCount(): number { return open?.length ?? 0; }

/** Queued-but-not-yet-freed count (probe surface). */
export function pendingDisposeCount(): number { return pendingOpenCount(); }
